import { TruncatedText, type ColumnDef as UiColumnDef } from "@coval/ui"

import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"

import type { ColumnDef } from "../types/columns"
import { isSortableColumn } from "../utils/runsTableState"
import {
  COLUMN_WIDTHS,
  isMetricColumn,
  METRIC_COLUMN_MIN_WIDTH,
} from "../utils/virtualRunsTableLayout"
import {
  RunCellContent,
  SimulationCellContent,
  type CellLookupProps,
} from "./RunsTableCells"

// The `tags` column width is computed from the loaded rows (see
// `computeTagsColumnWidth`); every other column uses the shared fixed widths.
// `minWidth` matches the width for fixed columns so the wide table keeps
// horizontal scrolling instead of compressing; metric columns flex from a 96px
// floor.
function widthFor(key: string, tagsColumnWidth: number): number {
  if (key === "tags") {
    return tagsColumnWidth
  }
  if (isMetricColumn(key)) {
    return COLUMN_WIDTHS.metrics
  }
  return COLUMN_WIDTHS[key] ?? COLUMN_WIDTHS.default
}

function minWidthFor(key: string, tagsColumnWidth: number): number {
  return isMetricColumn(key)
    ? METRIC_COLUMN_MIN_WIDTH
    : widthFor(key, tagsColumnWidth)
}

function TruncatedColumnHeader({ label }: { label: string }) {
  return (
    <TruncatedText className="w-full" side="top">
      {label}
    </TruncatedText>
  )
}

function headerFor(column: ColumnDef): UiColumnDef<unknown>["header"] {
  // The status column shows only an icon, so its header is left blank (the
  // "Status" label is still surfaced via `headerLabel` in the column toggle).
  if (column.key === "status") {
    return ""
  }
  return <TruncatedColumnHeader label={column.label} />
}

// Tags right-align to match the personas table; counts center under their label.
function alignFor(key: string): "left" | "right" | "center" | undefined {
  if (key === "tags") {
    return "right"
  }
  return key === "numSimulations" ? "center" : undefined
}

// The tags column width is derived from the loaded rows' tag counts
// (`computeTagsColumnWidth`), so it should stay exactly that width rather than
// flex to absorb leftover space — otherwise the empty (+ only) column stretches.
function flexFor(key: string): boolean | undefined {
  return key === "tags" ? false : undefined
}

// The keyset query supports server-side sorting on a fixed set of columns
// (created, agent, simulator, and the row's own ID); `isSortableColumn` is the
// single source of truth mapping those to the backend `sort_column`. Columns
// the backend can't sort (status, test set, persona, count, tags, metrics) get
// a plain header.
function sortableFor(key: string): boolean {
  return isSortableColumn(key)
}

// The ID columns are pinned to the right edge so the rest of the (wide) table
// scrolls horizontally underneath them.
const PINNED_RIGHT_KEYS = new Set(["id", "simulationId", "runId"])
function pinnedFor(key: string): "left" | "right" | undefined {
  return PINNED_RIGHT_KEYS.has(key) ? "right" : undefined
}

interface BuildColumnsParams {
  columns: ColumnDef[]
  tagsColumnWidth: number
  lookups: CellLookupProps
}

export function buildRunColumns({
  columns,
  tagsColumnWidth,
  lookups,
}: BuildColumnsParams): UiColumnDef<RunsTableResult>[] {
  return columns.map((column) => ({
    id: column.key,
    header: headerFor(column),
    headerLabel: column.label,
    width: widthFor(column.key, tagsColumnWidth),
    minWidth: minWidthFor(column.key, tagsColumnWidth),
    align: alignFor(column.key),
    flex: flexFor(column.key),
    pinned: pinnedFor(column.key),
    sortable: sortableFor(column.key),
    cell: (run: RunsTableResult) => (
      <RunCellContent columnKey={column.key} run={run} {...lookups} />
    ),
  }))
}

export function buildSimulationColumns({
  columns,
  tagsColumnWidth,
  lookups,
}: BuildColumnsParams): UiColumnDef<SimulationsTableResult>[] {
  return columns.map((column) => ({
    id: column.key,
    header: headerFor(column),
    headerLabel: column.label,
    width: widthFor(column.key, tagsColumnWidth),
    minWidth: minWidthFor(column.key, tagsColumnWidth),
    align: alignFor(column.key),
    flex: flexFor(column.key),
    pinned: pinnedFor(column.key),
    sortable: sortableFor(column.key),
    cell: (sim: SimulationsTableResult) => (
      <SimulationCellContent columnKey={column.key} sim={sim} {...lookups} />
    ),
  }))
}
